import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Cpu, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      if (err?.response) {
        setError(formatApiError(err, "Accesso non riuscito"));
      } else {
        setError("Impossibile contattare il server. Controlla la connessione e riprova tra qualche secondo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-[#0A0A0A]" data-testid="login-page">
      <img
        src="https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwc21hcnRwaG9uZXxlbnwwfHx8fDE3ODU5Mjc4MTZ8MA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 bg-primary flex items-center justify-center rounded-md">
              <Cpu size={26} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#F5F5F5] leading-none">TechGest</h1>
              <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mt-1.5">Gestionale Officina</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Nome</label>
              <input
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full px-4 py-3 rounded-md bg-[#0A0A0A] border border-[#333] text-[#F5F5F5] text-sm focus:outline-none focus:border-primary transition-colors duration-150"
                placeholder="Essedi"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full px-4 py-3 pr-11 rounded-md bg-[#0A0A0A] border border-[#333] text-[#F5F5F5] text-sm focus:outline-none focus:border-primary transition-colors duration-150"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white transition-colors duration-150"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-400 text-sm" data-testid="login-error-alert">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full py-3 rounded-md bg-primary text-white font-semibold text-sm hover:bg-blue-500 disabled:opacity-50 transition-colors duration-150"
            >
              {loading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
